import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Link,
  Checkbox,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Event as EventIcon,
  FilterList as FilterListIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { meetingService, Meeting, MeetingCreate } from '../services/meetingService';
import { wallClockToInput, inputToWallClock, formatWallClockDateTime } from '../utils/dateUtils';
import { getMeetingStatusColor as getStatusColor } from '../utils/statusUtils';
import { patientService, Patient } from '../services/patientService';
import { userService, User } from '../services/userService';
import { meetingTypeService, MeetingType } from '../services/meetingTypeService';
import { RunMeetingSummaryDialog } from '../components/dialogs/RunMeetingSummaryDialog';
import { patientLabel } from '../utils/patientLabel';

const MeetingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { currentTenant, refreshKey } = useTenant();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [therapists, setTherapists] = useState<User[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);

  // Filter State
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<string>('today');

  // UI State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [runSummaryDialogOpen, setRunSummaryDialogOpen] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringCount, setRecurringCount] = useState<number>(1);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<MeetingCreate>();

  // Load data
  useEffect(() => {
    const loadMeetingsData = async () => {
      if (!user || !currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load meetings, patients, and meeting types (always needed)
        const [meetingsData, patientsData, meetingTypesData] = await Promise.all([
          meetingService.getMeetings(
            currentTenant.id,
            undefined,
            undefined,
            statusFilter as any,
            dateRangeFilter
          ),
          patientService.getAllPatients(currentTenant.id),
          meetingTypeService.getMeetingTypes(currentTenant.id),
        ]);

        setMeetings(meetingsData);
        setPatients(patientsData);
        setMeetingTypes(meetingTypesData);

        // Load all therapists — any user can schedule for any therapist.
        try {
          const usersData = await userService.getUsers(currentTenant.id);
          setTherapists(usersData);
        } catch (err) {
          console.error('Error loading therapists:', err);
          setTherapists([]);
        }
      } catch (err) {
        console.error('Error loading meetings data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load meetings data');
      } finally {
        setLoading(false);
      }
    };

    loadMeetingsData();
  }, [user, currentTenant, refreshKey, statusFilter, dateRangeFilter]);

  // Handle URL parameters for direct scheduling
  useEffect(() => {
    const patientId = searchParams.get('patient_id');
    const action = searchParams.get('action');

    if (action === 'schedule' && patientId && patients.length > 0 && currentTenant && therapists.length > 0) {
      const patientIdNum = parseInt(patientId);
      const patient = patients.find(p => p.id === patientIdNum);
      if (patient) {
        // Default to the current user, falling back to the first therapist.
        const defaultTherapistId = user?.id || therapists[0]?.id || 0;

        // Auto-open the schedule dialog with pre-selected patient and default therapist
        setEditingMeeting(null);
        reset({
          title: `Session - ${patient.full_name}`,
          scheduled_at: '',
          duration_minutes: 50,
          notes: '',
          fee: 0,
          patient_id: patientIdNum,
          therapist_id: defaultTherapistId,
          tenant_id: currentTenant.id,
          meeting_type_id: undefined,
        });
        setAddDialogOpen(true);
        // Clear URL parameters
        navigate('/meetings', { replace: true });
      }
    }
  }, [patients, currentTenant, searchParams, user, reset, navigate, therapists]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, meeting: Meeting) => {
    setAnchorEl(event.currentTarget);
    setSelectedMeeting(meeting);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMeeting(null);
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, meeting: Meeting) => {
    setStatusAnchorEl(event.currentTarget);
    setSelectedMeeting(meeting);
  };

  const handleStatusMenuClose = () => {
    setStatusAnchorEl(null);
    setSelectedMeeting(null);
  };

  const handleViewDetails = () => {
    if (selectedMeeting) {
      navigate(`/meetings/${selectedMeeting.id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedMeeting) {
      setEditingMeeting(selectedMeeting);
      reset({
        title: selectedMeeting.title,
        scheduled_at: wallClockToInput(selectedMeeting.scheduled_at),
        duration_minutes: selectedMeeting.duration_minutes,
        notes: selectedMeeting.notes || '',
        fee: selectedMeeting.fee || 0,
        patient_id: selectedMeeting.patient_id ?? undefined,
        therapist_id: selectedMeeting.therapist_id,
        tenant_id: selectedMeeting.tenant_id,
        meeting_type_id: selectedMeeting.meeting_type_id || undefined,
      });
      setAddDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleRunSummary = () => {
    setRunSummaryDialogOpen(true);
    handleMenuClose();
  };

  const handleUpdateStatus = async (status: 'active' | 'noshow' | 'cancelled' | 'done') => {
    if (!selectedMeeting) return;

    try {
      await meetingService.updateMeetingStatus(selectedMeeting.id, status);
      toast.success(`Meeting marked as ${status}`);

      // Update local state
      setMeetings(meetings.map(m =>
        m.id === selectedMeeting.id
          ? { ...m, status }
          : m
      ));
    } catch (error) {
      console.error('Error updating meeting status:', error);
      toast.error('Failed to update meeting status');
    }
    handleStatusMenuClose();
  };

  const handleAddMeeting = () => {
    // Default to the current user, falling back to the first therapist.
    const defaultTherapistId = user?.id || therapists[0]?.id || 0;

    setEditingMeeting(null);
    reset({
      title: '',
      scheduled_at: '',
      duration_minutes: 50,
      notes: '',
      fee: 0,
      patient_id: 0,
      therapist_id: defaultTherapistId,
      tenant_id: currentTenant?.id || 0,
    });
    setAddDialogOpen(true);
  };

  const onSubmit = async (data: MeetingCreate) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    try {
      const meetingData = {
        ...data,
        // Convert user timezone datetime input to UTC for server
        scheduled_at: inputToWallClock(data.scheduled_at),
        tenant_id: currentTenant.id,
      };

      if (editingMeeting) {
        // Update existing meeting
        const updatedMeeting = await meetingService.updateMeeting(editingMeeting.id, meetingData);
        setMeetings(meetings.map(m => m.id === editingMeeting.id ? updatedMeeting : m));
        toast.success('Meeting updated successfully');
      } else {
        // Create new meeting(s)
        if (isRecurring && recurringCount > 1) {
          const newMeetings: Meeting[] = [];
          const baseDate = new Date(inputToWallClock(data.scheduled_at));

          for (let i = 0; i < recurringCount; i++) {
            const meetingDate = new Date(baseDate);
            meetingDate.setDate(meetingDate.getDate() + (i * 7)); // Add weeks

            const recurringMeetingData = {
              ...meetingData,
              scheduled_at: meetingDate.toISOString(),
            };

            try {
              const newMeeting = await meetingService.createMeeting(recurringMeetingData);
              newMeetings.push(newMeeting);
            } catch (error) {
              console.error(`Error creating meeting ${i + 1}:`, error);
              toast.error(`Failed to create meeting ${i + 1} of ${recurringCount}`);
            }
          }

          setMeetings([...meetings, ...newMeetings]);
          toast.success(`${newMeetings.length} recurring meetings created successfully`);
        } else {
          // Create single meeting
          const newMeeting = await meetingService.createMeeting(meetingData);
          setMeetings([...meetings, newMeeting]);
          toast.success('Meeting created successfully');
        }
      }

      setAddDialogOpen(false);
      setEditingMeeting(null);
      setIsRecurring(false);
      setRecurringCount(1);
      reset();
    } catch (error) {
      console.error('Error saving meeting:', error);
      toast.error('Failed to save meeting');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading meetings: {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Meetings ({meetings.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddMeeting}
          disabled={!currentTenant}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            padding: { xs: '6px 16px', sm: '8px 22px' }
          }}
        >
          Schedule Meeting
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Filters:
            </Typography>
          </Box>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="done">Done</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="noshow">No Show</MenuItem>
            </Select>
          </FormControl>

          {/* Date Range Filter */}
          <ToggleButtonGroup
            value={dateRangeFilter}
            exclusive
            onChange={(e, newValue) => newValue && setDateRangeFilter(newValue)}
            size="small"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              '& .MuiToggleButton-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 }
              }
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="tomorrow">Tomorrow</ToggleButton>
            <ToggleButton value="thisWeek">This Week</ToggleButton>
            <ToggleButton value="lastWeek">Last Week</ToggleButton>
            <ToggleButton value="nextWeek">Next Week</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Mobile Card View */}
      {isMobile ? (
        <Grid container spacing={2}>
          {meetings.map((meeting) => (
            <Grid item xs={12} key={meeting.id}>
              <Card sx={{ position: 'relative' }}>
                <CardContent sx={{ pb: 1 }}>
                  {/* Three-dot menu */}
                  <IconButton
                    onClick={(e) => handleMenuClick(e, meeting)}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>

                  {/* Header with title and status */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
                    <Box display="flex" alignItems="center" flex={1}>
                      <EventIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
                      <Box>
                        <Button
                          variant="text"
                          sx={{
                            p: 0,
                            fontSize: '1.1rem',
                            fontWeight: 'medium',
                            textTransform: 'none',
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                            display: 'block',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => navigate(`/meetings/${meeting.id}`)}
                        >
                          {meeting.title}
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          {formatWallClockDateTime(meeting.scheduled_at)}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={meeting.status}
                      color={getStatusColor(meeting.status) as any}
                      size="small"
                      onClick={(e) => handleStatusClick(e, meeting)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>

                  {/* Meeting Details */}
                  <Box sx={{ pl: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      👤{' '}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => meeting.patient && navigate(`/patients/${meeting.patient.id}`)}
                        sx={{ textAlign: 'left', color: 'text.secondary',
                              cursor: meeting.patient ? 'pointer' : 'default',
                              textDecoration: meeting.patient ? undefined : 'none' }}
                      >
                        {patientLabel(meeting)}
                      </Link>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      👩‍⚕️ {meeting.therapist.full_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ⏱️ {meeting.duration_minutes} minutes
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {meetings.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  No meetings found
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Therapist</TableCell>
                <TableCell>Meeting Type</TableCell>
                <TableCell>Scheduled At</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {meetings.map((meeting) => (
                <TableRow key={meeting.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <EventIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <Button
                        variant="text"
                        sx={{
                          p: 0,
                          fontSize: '0.875rem',
                          textTransform: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/meetings/${meeting.id}`)}
                      >
                        {meeting.title}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => meeting.patient && navigate(`/patients/${meeting.patient.id}`)}
                      sx={{ textAlign: 'left',
                            cursor: meeting.patient ? 'pointer' : 'default',
                            textDecoration: meeting.patient ? undefined : 'none' }}
                    >
                      {patientLabel(meeting)}
                    </Link>
                  </TableCell>
                  <TableCell>{meeting.therapist.full_name}</TableCell>
                  <TableCell>
                    {meeting.meeting_type ? meeting.meeting_type.name : '-'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="text"
                      sx={{
                        p: 0,
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/meetings/${meeting.id}`)}
                    >
                      {formatWallClockDateTime(meeting.scheduled_at)}
                    </Button>
                  </TableCell>
                  <TableCell>{meeting.duration_minutes} min</TableCell>
                  <TableCell>
                    <Chip
                      label={meeting.status}
                      color={getStatusColor(meeting.status) as any}
                      size="small"
                      onClick={(e) => handleStatusClick(e, meeting)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => handleMenuClick(e, meeting)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <EventIcon sx={{ mr: 2 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        {user?.user_privilege?.prompt_development && (
          <MenuItem onClick={handleRunSummary}>
            <AutoFixHighIcon sx={{ mr: 2 }} />
            Run Summary
          </MenuItem>
        )}
      </Menu>

      {/* Status Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusMenuClose}
      >
        {selectedMeeting?.status === 'active' && (
          <>
            <MenuItem onClick={() => handleUpdateStatus('done')}>Mark as Done</MenuItem>
            <MenuItem onClick={() => handleUpdateStatus('cancelled')}>Cancel</MenuItem>
            <MenuItem onClick={() => handleUpdateStatus('noshow')}>Mark as No-Show</MenuItem>
          </>
        )}
        {(selectedMeeting?.status === 'noshow' || selectedMeeting?.status === 'cancelled' || selectedMeeting?.status === 'done') && (
          <MenuItem onClick={() => handleUpdateStatus('active')}>Reactivate</MenuItem>
        )}
      </Menu>

      {selectedMeeting && (
        <RunMeetingSummaryDialog
          open={runSummaryDialogOpen}
          onClose={() => setRunSummaryDialogOpen(false)}
          meetingId={selectedMeeting.id}
          meetingTypeId={selectedMeeting.meeting_type_id}
        />
      )}

      {/* Add/Edit Meeting Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingMeeting ? 'Edit Meeting' : 'Schedule New Meeting'}
          </DialogTitle>
          <DialogContent>
            <TextField
              {...register('title', { required: 'Title is required' })}
              fullWidth
              label="Title"
              margin="normal"
              error={!!errors.title}
              helperText={errors.title?.message as string}
            />
            <FormControl fullWidth margin="normal" error={!!errors.patient_id}>
              <InputLabel>Patient</InputLabel>
              <Select
                {...register('patient_id', { required: 'Patient is required', valueAsNumber: true })}
                label="Patient"
                value={watch('patient_id') || ''}
                onChange={(e) => setValue('patient_id', e.target.value as number)}
              >
                {patients.map((patient) => (
                  <MenuItem key={patient.id} value={patient.id}>
                    {patient.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" error={!!errors.therapist_id}>
              <InputLabel>Therapist</InputLabel>
              <Select
                {...register('therapist_id', { required: 'Therapist is required', valueAsNumber: true })}
                label="Therapist"
                value={watch('therapist_id') || ''}
                onChange={(e) => setValue('therapist_id', e.target.value as number)}
              >
                {therapists.map((therapist) => (
                  <MenuItem key={therapist.id} value={therapist.id}>
                    {therapist.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              {...register('scheduled_at', { required: 'Scheduled time is required' })}
              fullWidth
              label="Scheduled At"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              margin="normal"
              error={!!errors.scheduled_at}
              helperText={errors.scheduled_at?.message as string}
            />
            {!editingMeeting && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                    />
                  }
                  label="Recurring Weekly Meeting"
                  sx={{ mt: 2 }}
                />
                {isRecurring && (
                  <TextField
                    fullWidth
                    label="Number of Meetings"
                    type="number"
                    value={recurringCount}
                    onChange={(e) => setRecurringCount(Math.max(1, parseInt(e.target.value) || 1))}
                    margin="normal"
                    inputProps={{ min: 1, max: 52 }}
                    helperText="How many weekly meetings to create (max 52)"
                  />
                )}
              </>
            )}
            <TextField
              {...register('duration_minutes', { valueAsNumber: true })}
              fullWidth
              label="Duration (minutes)"
              type="number"
              margin="normal"
              defaultValue={50}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Meeting Type</InputLabel>
              <Select
                {...register('meeting_type_id', { valueAsNumber: true })}
                label="Meeting Type"
                value={watch('meeting_type_id') || ''}
                onChange={(e) => setValue('meeting_type_id', e.target.value ? Number(e.target.value) : undefined)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {meetingTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              {...register('fee', { valueAsNumber: true })}
              fullWidth
              label="Fee"
              type="number"
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              {...register('notes')}
              fullWidth
              label="Notes"
              multiline
              rows={3}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingMeeting ? 'Update' : 'Schedule'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default MeetingsPage;